import { useEffect, useState } from "react";
import { AlertTriangle, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { P } from "@/components/studio/portalKit";

const RollCard = ({ r, onReorder }) => {
  const [open, setOpen] = useState(false);
  const used = Math.max(0, (r.total_capacity || 0) - r.balance_sqft);
  return (
    <Card data-testid={`roll-${r.id}`} className={r.low ? "border-amber-300" : r.status === "replaced" || r.balance_sqft <= 0 ? "opacity-60" : ""}>
      <CardContent className="space-y-3 p-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <p className="font-semibold text-brand-ink">{r.serial}</p>
            <p className="text-xs text-slate-500">{r.category} · {r.product_name || "—"}{r.status === "replaced" && ` · Replaced by ${r.replaced_by}`}</p>
          </div>
          <p data-testid={`roll-left-${r.id}`} className={`text-right text-sm font-bold ${r.low ? "text-amber-700" : "text-brand-ink"}`}>
            {r.balance_sqft} sqft left<span className="block text-xs font-normal text-slate-500">of {r.total_capacity || 0} · {used.toFixed(1)} used</span>
          </p>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-slate-100">
          <div className={`h-full ${r.low ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(100, r.pct_left)}%` }} />
        </div>
        {r.low && (
          <div data-testid={`roll-low-${r.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <span className="flex items-center gap-1.5"><AlertTriangle className="h-4 w-4" />Running low — under 20% left</span>
            <Button size="sm" data-testid={`roll-reorder-${r.id}`} className="h-9 bg-crimson-500 hover:bg-crimson-600" onClick={onReorder}>Reorder</Button>
          </div>
        )}
        <button type="button" data-testid={`roll-uses-toggle-${r.id}`} onClick={() => setOpen(!open)}
          className="flex items-center gap-1 text-sm font-medium text-slate-600 hover:text-brand-ink">
          <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />Used in {r.uses.length} registration{r.uses.length === 1 ? "" : "s"}
        </button>
        {open && (
          <div className="space-y-1 rounded-lg bg-slate-50 p-3 text-sm">
            {r.uses.length === 0 && <p className="text-slate-500">Not used yet.</p>}
            {r.uses.map((u, i) => (
              <div key={i} className="flex justify-between gap-3"><span>{u.label}{u.date && <span className="text-xs text-slate-500"> · {u.date}</span>}</span><span className="font-medium">{u.sqft} sqft</span></div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const MyRolls = ({ onReorder }) => {
  const [rolls, setRolls] = useState(null);
  useEffect(() => {
    const rank = (r) => (r.low ? 0 : r.status === "replaced" || r.balance_sqft <= 0 ? 2 : 1);
    api.get(`${P}/rolls`).then(({ data }) => setRolls([...data].sort((a, b) => rank(a) - rank(b))))
      .catch(() => { setRolls([]); toast.error("Couldn't load your rolls"); });
  }, []);
  if (!rolls) return <p className="text-sm text-slate-500">Loading…</p>;
  const low = rolls.filter((r) => r.low).length;
  return (
    <div className="space-y-3">
      {rolls.length === 0 && <p className="text-sm text-slate-500">No rolls delivered yet.</p>}
      {low > 0 && <p data-testid="rolls-low-summary" className="text-sm font-semibold text-amber-700">{low} roll{low === 1 ? " is" : "s are"} below 20% — reorder soon.</p>}
      <div className="grid gap-3 lg:grid-cols-2">{rolls.map((r) => <RollCard key={r.id} r={r} onReorder={onReorder} />)}</div>
    </div>
  );
};
